import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { FilterToggle, FiltersPanel } from '@components/filters';
import { getPublicPlans, deletePublicPlan, isPlanDeleted, type PublicPlan } from '@data/publicPlans';
import { acceptOffer as acceptOfferLocal, getOffers, rejectOffer as rejectOfferLocal, type Offer } from '@data/offers';
import { stylePresetsByKey } from '@data/stylePresets';
import { useAlertDialog } from '../../shared/AlertDialog';
import { useAuth } from '@services/auth';
import { useAdminViewAs, getAdminEffectiveMyPlansOwnerUserIdNum } from '../../lib/adminViewAs';
import { getPlans } from '@services/plans';
import { getOffersForPlan as getOffersForPlanAPI, getOffersForSeeker, acceptOffer as acceptOfferAPI, rejectOffer as rejectOfferAPI } from '@services/offers';
import { getMySeekerPlanInvites, declinePlanInvite, claimPlanInvite, getMySeekerDirectPlans, type SeekerPlanInviteSummary } from '@services/planInvites';
import { getImageUrl } from '@utils/imageUrl';
import { mapApiPlanToPublicPlan } from '@utils/mapApiPlanToPublicPlan';
import { PLAN_CARD_FALLBACK_IMAGE, getPlanCardCoverCandidates } from '@utils/planCoverImage';
import { mapBackendOfferToFrontend, dedupeOffersForDisplay } from './OffersForMyPlan';
import OfferDetailPreview from '@components/OfferDetailPreview';
import ConversationThread from '@components/ConversationThread';
import { useI18n } from '../../i18n';

function getStatusBadgeClass(status: PublicPlan['status']) {
	if (status === 'active') return 'text-bg-success';
	if (status === 'draft') return 'text-bg-secondary';
	return 'text-bg-warning';
}

/** Use db- format for database plans so PlanDetails fetches latest revisions from API */
function planDetailId(plan: PublicPlan): string {
	return plan.databaseId ? `db-${plan.databaseId}` : plan.id;
}

/**
 * Merge API + localStorage plans without hiding distinct DB rows.
 * - Rows with databaseId: keep each id once only (true duplicate rows).
 * - Rows without databaseId: skip if the same user/title/city/day already has a DB plan (local echo), else dedupe among locals only.
 */
function dedupePlansForDisplay(plans: PublicPlan[]): PublicPlan[] {
	const contentKey = (p: PublicPlan) => {
		const date = (p.createdAt || '').slice(0, 10); // YYYY-MM-DD only so time format doesn't split same day
		return `${p.seekerUserId}|${(p.title || '').trim()}|${(p.city || '').trim()}|${date}`;
	};
	const seenDbIds = new Set<number>();
	const contentKeysWithDbPlan = new Set<string>();
	const seenContentKeysLocalOnly = new Set<string>();
	const sorted = [...plans].sort((a, b) => (b.databaseId != null ? 1 : 0) - (a.databaseId != null ? 1 : 0));
	const result: PublicPlan[] = [];
	for (const p of sorted) {
		if (p.databaseId != null) {
			if (seenDbIds.has(p.databaseId)) continue;
			seenDbIds.add(p.databaseId);
			contentKeysWithDbPlan.add(contentKey(p));
			result.push(p);
			continue;
		}
		const key = contentKey(p);
		if (contentKeysWithDbPlan.has(key)) continue;
		if (seenContentKeysLocalOnly.has(key)) continue;
		seenContentKeysLocalOnly.add(key);
		result.push(p);
	}
	return result;
}

/** Plan list thumbnail: after → before → fallback (see {@link getPlanCardCoverCandidates}). */
function MyPlanCardCoverImage({ plan, alt }: { plan: PublicPlan; alt: string }) {
	const candidatesKey = [
		plan.id,
		plan.status,
		plan.photoBeforeUrl,
		plan.questionnaire?.photoBeforeUrl,
		plan.aiGenerated?.photoAfterUrl
	].join('\u0001');
	const resolved = useMemo(() => {
		const list = getPlanCardCoverCandidates(plan);
		const out: string[] = [];
		const seen = new Set<string>();
		for (const raw of list) {
			const r = (raw || '').trim();
			if (!r) continue;
			const abs = getImageUrl(r) || r;
			if (seen.has(abs)) continue;
			seen.add(abs);
			out.push(abs);
		}
		return out.length > 0 ? out : [PLAN_CARD_FALLBACK_IMAGE];
		// eslint-disable-next-line react-hooks/exhaustive-deps -- serialized plan image inputs
	}, [candidatesKey]);
	const [index, setIndex] = useState(0);
	useEffect(() => {
		setIndex(0);
	}, [candidatesKey]);
	const src = resolved[index] ?? resolved[0];
	/* Direct child of .ratio: Bootstrap sets position/size on .ratio > * — do not wrap in another box. */
	return (
		<img
			key={`${plan.id}-${index}-${src}`}
			src={src}
			alt={alt}
			className="rounded-0"
			style={{ objectFit: 'cover' }}
			onError={() => setIndex((i) => (i < resolved.length - 1 ? i + 1 : i))}
		/>
	);
}

export default function MyPlans() {
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTab = searchParams.get('tab') || 'all';
	const { showAlert } = useAlertDialog();
	const navigate = useNavigate();
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const localizeOfferStatus = (status: Offer['status']) =>
		status === 'accepted'
			? copy('accepted', 'akzeptiert', 'prihvaćeno')
			: status === 'rejected'
				? copy('rejected', 'abgelehnt', 'odbijeno')
				: copy('pending', 'ausstehend', 'na čekanju');
	const localizePlanStatus = (status: PublicPlan['status']) =>
		status === 'active'
			? copy('Active', 'Aktiv', 'Aktivni')
			: status === 'draft'
				? copy('Draft', 'Entwurf', 'Skica')
				: copy('Closed', 'Geschlossen', 'Zatvoreno');
	const localizeStyleLabel = (styleKey: PublicPlan['styleKey'] | string | undefined) => {
		switch (styleKey) {
			case 'modern': return copy('Modern', 'Modern', 'Moderni');
			case 'mediterranean': return copy('Mediterranean', 'Mediterran', 'Mediteranski');
			case 'minimalist': return copy('Minimalist', 'Minimalistisch', 'Minimalistički');
			case 'cottage': return copy('Cottage', 'Landhaus', 'Vikendica');
			case 'japanese': return copy('Japanese Zen', 'Japanischer Zen', 'Japanski Zen');
			case 'tropical': return copy('Tropical', 'Tropisch', 'Tropski');
			case 'rustic': return copy('Rustic', 'Rustikal', 'Rustikalni');
			case 'coastal': return copy('Coastal', 'Küstenstil', 'Obalni');
			case 'scandinavian': return copy('Scandinavian', 'Skandinavisch', 'Skandinavski');
			case 'traditional': return copy('Traditional', 'Traditionell', 'Tradicionalni');
			default: return stylePresetsByKey[styleKey as keyof typeof stylePresetsByKey]?.title ?? String(styleKey || '');
		}
	};
	const [plans, setPlans] = useState<PublicPlan[]>(() => dedupePlansForDisplay(getPublicPlans()));
	const [offers, setOffers] = useState<Offer[]>(() => getOffers());
	const [apiOffersByPlanId, setApiOffersByPlanId] = useState<Record<string, Offer[]>>({});
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedStatuses, setSelectedStatuses] = useState<Array<PublicPlan['status']>>([]);
	const [selectedStyles, setSelectedStyles] = useState<Array<PublicPlan['styleKey']>>([]);
	const [selectedCities, setSelectedCities] = useState<string[]>([]);
	const [hasOffersFilter, setHasOffersFilter] = useState<'any' | 'with' | 'without'>('any');
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'city' | 'style'>('newest');
	const [photoFilter, setPhotoFilter] = useState<'any' | 'with' | 'without'>('any');
	const [aiFilter, setAIFilter] = useState<'any' | 'with' | 'without'>('any');

	// Company invite banner state for seekers
	const [pendingInvites, setPendingInvites] = useState<SeekerPlanInviteSummary[]>([]);
	const [isLoadingInvites, setIsLoadingInvites] = useState(false);
	const [inviteError, setInviteError] = useState<string | null>(null);
	const [hasClaimedInviteFromUrl, setHasClaimedInviteFromUrl] = useState(false);
	
	// Offer-specific filters
	const [selectedOfferStatuses, setSelectedOfferStatuses] = useState<Array<Offer['status']>>([]);
	const [minMidBudget, setMinMidBudget] = useState('');
	const [maxMaxBudget, setMaxMaxBudget] = useState('');
	const [offerSearchTerm, setOfferSearchTerm] = useState('');
	const [expandedOfferIds, setExpandedOfferIds] = useState<Set<string>>(new Set());
	const [messageOfferId, setMessageOfferId] = useState<string | null>(null);
	
	const planOwnerUserIdNum = useMemo(
		() => getAdminEffectiveMyPlansOwnerUserIdNum(user, viewAsTarget),
		[user, viewAsTarget]
	);

	// Get current user ID - use actual user ID or fallback to demo
	const currentUserId =
		planOwnerUserIdNum != null
			? String(planOwnerUserIdNum)
			: user?.id
				? String(user.id)
				: 'u-seeker-demo';

	const seekerFirstName = useMemo(() => {
		if (user?.role === 'admin' && viewAsTarget?.name) {
			return viewAsTarget.name.split(' ')[0];
		}
		return user?.name ? user.name.split(' ')[0] : 'there';
	}, [user?.name, user?.role, viewAsTarget]);

	// Detect invite code from URL (?invite=ABC123 or ?code=ABC123)
	const inviteCodeFromUrl = useMemo(() => {
		const raw = searchParams.get('invite') || searchParams.get('code');
		return raw && raw.trim() ? raw.trim() : null;
	}, [searchParams]);

	const offerIdFromUrl = useMemo(() => {
		const raw = searchParams.get('offer_id');
		if (!raw || !raw.trim()) return null;
		const n = parseInt(raw.trim(), 10);
		return Number.isFinite(n) && n > 0 ? String(n) : null;
	}, [searchParams]);

	useEffect(() => {
		const handlePlansUpdate = () => {
			// When logged in, API is source of truth; don't overwrite with localStorage or the duplicate reappears
			if (user?.id) return;
			setPlans(dedupePlansForDisplay(getPublicPlans()));
		};
		const handleOffersUpdate = () => setOffers(getOffers());

		window.addEventListener('publicPlansUpdated', handlePlansUpdate);
		window.addEventListener('offersUpdated', handleOffersUpdate);

		// Load plans from localStorage (deduped so same plan never appears twice)
		setPlans(dedupePlansForDisplay(getPublicPlans()));
		setOffers(getOffers());
		
		// Also try to fetch plans from database when we know the seeker plan owner (self or admin view-as)
		if (planOwnerUserIdNum != null) {
			const userIdNum = planOwnerUserIdNum;
			if (!isNaN(userIdNum)) {
				getPlans(userIdNum, {
					includeHidden: user?.role === 'admin',
					includeDeleted: user?.role === 'admin'
				})
					.then(async (dbPlans) => {
						// Use API as single source of truth for DB plans so the same plan never appears twice.
						// Build list from API only, then add local-only plans (not yet in API).
						// IMPORTANT: use mapApiPlanToPublicPlan so aiGenerated / after_image_url match PlanDetails
						// (a hand-rolled map omitted aiGenerated and made covers flip after → before when API loaded).
						const dbPlanIds = new Set(dbPlans.map((p) => p.id));
						const fromApi: PublicPlan[] = dbPlans.map((dbPlan) => ({
							...mapApiPlanToPublicPlan(dbPlan),
							canEdit: true
						}));
						const sharedDirect = await getMySeekerDirectPlans(userIdNum).catch(() => []);
						const sharedPlans: PublicPlan[] = sharedDirect.map((dbPlan: any) => ({
							...mapApiPlanToPublicPlan(dbPlan),
							seekerUserId: String(userIdNum),
							canEdit: dbPlan.invite_status === 'used' ? Boolean(dbPlan.seeker_can_edit ?? true) : false,
							inviteId: dbPlan.invite_id,
							inviteCode: dbPlan.invite_code,
							inviteStatus: dbPlan.invite_status,
							sharedByCompanyName: dbPlan.company_name
						}));
						const localPlans = getPublicPlans();
						const localOnly = localPlans.filter(
							(p) => p.seekerUserId === currentUserId && (p.databaseId == null || !dbPlanIds.has(p.databaseId))
						);
						setPlans(dedupePlansForDisplay([...fromApi, ...sharedPlans, ...localOnly]));
					})
					.catch((err) => {
						console.warn('Failed to fetch plans from database:', err);
						// Continue with localStorage plans only
					});
			}
		}

		return () => {
			window.removeEventListener('publicPlansUpdated', handlePlansUpdate);
			window.removeEventListener('offersUpdated', handleOffersUpdate);
		};
	}, [user, planOwnerUserIdNum, viewAsTarget, currentUserId]);

	// Load seeker-specific pending invites (matched by email) so we can show a banner on login.
	useEffect(() => {
		if (!user?.id) {
			setPendingInvites([]);
			return;
		}
		// Only relevant for seekers
		if (planOwnerUserIdNum == null) {
			setPendingInvites([]);
			return;
		}
		const rawId = planOwnerUserIdNum;
		const userIdNum = typeof rawId === 'number' ? rawId : parseInt(String(rawId), 10);
		if (!userIdNum || Number.isNaN(userIdNum)) {
			setPendingInvites([]);
			return;
		}

		let cancelled = false;
		setIsLoadingInvites(true);
		setInviteError(null);

		getMySeekerPlanInvites(userIdNum)
			.then((list) => {
				if (cancelled) return;
				// Only keep pending invites; backend already filters by status/expiry, but be defensive.
				const pending = (list ?? []).filter((inv) => inv.status === 'pending');
				setPendingInvites(pending);
			})
			.catch((err) => {
				if (cancelled) return;
				console.warn('Failed to load seeker plan invites:', err);
				setInviteError('We could not load your invitations right now.');
				setPendingInvites([]);
			})
			.finally(() => {
				if (!cancelled) setIsLoadingInvites(false);
			});

		return () => {
			cancelled = true;
		};
	}, [user, planOwnerUserIdNum]);

	// If there is an invite code in the URL and we have a logged-in seeker,
	// "claim" this invite so it is linked to their account (used_by_user_id),
	// which makes it show up on future logins even without the link.
	useEffect(() => {
		if (!inviteCodeFromUrl || hasClaimedInviteFromUrl) return;
		if (!user?.id || user.role !== 'seeker') return;
		const rawId = user.id;
		const userIdNum = typeof rawId === 'number' ? rawId : parseInt(String(rawId), 10);
		if (!userIdNum || Number.isNaN(userIdNum)) return;

		let cancelled = false;
		claimPlanInvite(userIdNum, inviteCodeFromUrl)
			.then((summary) => {
				if (cancelled) return;
				// Merge claimed invite into local pending list if it's still pending
				if (summary.status === 'pending') {
					setPendingInvites((prev) => {
						if (prev.some((inv) => inv.id === summary.id)) return prev;
						return [...prev, summary];
					});
				}
				setHasClaimedInviteFromUrl(true);
				if (offerIdFromUrl && summary.plan_id) {
					navigate(
						`/app/seeker/plan/db-${summary.plan_id}/offers?offer_id=${encodeURIComponent(offerIdFromUrl)}`
					);
				}
			})
			.catch((err) => {
				if (cancelled) return;
				console.warn('Failed to claim invite from URL:', err);
				// Non-fatal – banner will still show if email-matched.
				setHasClaimedInviteFromUrl(true);
			});

		return () => {
			cancelled = true;
		};
	}, [inviteCodeFromUrl, user, hasClaimedInviteFromUrl, offerIdFromUrl, navigate]);

	// Load all offers for this seeker from API (so Offers tab shows real offers even if plans not in state yet)
	useEffect(() => {
		if (planOwnerUserIdNum == null) return;
		const userIdNum = planOwnerUserIdNum;
		if (isNaN(userIdNum)) return;
		let cancelled = false;
		getOffersForSeeker(userIdNum)
			.then((list) => {
				if (cancelled) return;
				const byPlan: Record<string, Offer[]> = {};
				list.forEach((backendOffer) => {
					const planId = `db-${backendOffer.plan_id}`;
					if (!byPlan[planId]) byPlan[planId] = [];
					byPlan[planId].push(mapBackendOfferToFrontend(backendOffer));
				});
				for (const planId of Object.keys(byPlan)) {
					byPlan[planId] = dedupeOffersForDisplay(byPlan[planId]);
				}
				setApiOffersByPlanId(byPlan);
			})
			.catch((err) => {
				if (!cancelled) console.warn('Failed to fetch offers for seeker:', err);
				setApiOffersByPlanId({});
			});
		return () => { cancelled = true; };
	}, [planOwnerUserIdNum]);

	const myPlans = useMemo(
		() => {
			// Admin on seeker "My Plans" without a lens: do not show plans keyed to the admin user id (stale localStorage / wrong account).
			if (user?.role === 'admin' && !viewAsTarget) {
				return [];
			}
			// Filter by user ID, exclude hidden plans, and exclude deleted plans
			let filtered = plans.filter(p => {
				// Check user ID first
				if (p.seekerUserId !== currentUserId) {
					return false;
				}
				
				// Check if plan is deleted (either in localStorage or database)
				if (isPlanDeleted(p.id, p.databaseId)) {
					return false;
				}
				
				// Check moderation status - exclude hidden and deleted
				if (p.moderationStatus === 'hidden' || p.moderationStatus === 'deleted') {
					return false;
				}
				
				return true;
			});
			filtered = dedupePlansForDisplay(filtered);
			// Apply tab filter
			if (activeTab === 'drafts') {
				return filtered.filter(p => p.status === 'draft');
			}
			if (activeTab === 'active') {
				return filtered.filter(p => p.status === 'active');
			}
			if (activeTab === 'closed') {
				return filtered.filter(p => p.status === 'closed');
			}
			return filtered;
		},
		[plans, activeTab, currentUserId, user?.role, viewAsTarget]
	);

	const offersByPlan = useMemo(() => {
		const local = offers.reduce((acc, offer) => {
			if (!acc[offer.planId]) acc[offer.planId] = [];
			acc[offer.planId].push(offer);
			return acc;
		}, {} as Record<string, Offer[]>);
		const merged: Record<string, Offer[]> = { ...local };
		for (const [planId, apiList] of Object.entries(apiOffersByPlanId)) {
			merged[planId] = dedupeOffersForDisplay(apiList.length > 0 ? apiList : (merged[planId] ?? []));
		}
		for (const planId of Object.keys(merged)) {
			if (!apiOffersByPlanId[planId]) {
				merged[planId] = dedupeOffersForDisplay(merged[planId]);
			}
		}
		return merged;
	}, [offers, apiOffersByPlanId]);

	// Get all offers for seeker's plans (from API and localStorage); include offers even when plan not in myPlans yet (use planTitle/planCity from offer)
	const myOffers = useMemo(() => {
		const result: Array<Offer & { plan: PublicPlan }> = [];
		for (const p of myPlans) {
			const list = offersByPlan[p.id] ?? [];
			for (const o of list) result.push({ ...o, plan: p });
		}
		// Include offers from apiOffersByPlanId for plan ids not in myPlans (e.g. plans not yet loaded)
		for (const [planId, list] of Object.entries(apiOffersByPlanId)) {
			if (myPlans.some((p) => p.id === planId)) continue;
			const first = list[0];
			const syntheticPlan: PublicPlan = {
				id: planId,
				databaseId: planId.startsWith('db-') ? parseInt(planId.slice(3), 10) : undefined,
				seekerUserId: currentUserId,
				title: first?.planTitle ?? 'Plan',
				city: first?.planCity ?? '',
				contactEmail: '',
				styleKey: 'modern',
				status: 'active',
				createdAt: first?.createdAt ?? new Date().toISOString(),
				moderationStatus: 'visible'
			};
			for (const o of list) result.push({ ...o, plan: syntheticPlan });
		}
		return result.sort((a, b) => {
			const statusOrder: Record<Offer['status'], number> = { pending: 0, accepted: -1, rejected: 1 };
			const statusDiff = statusOrder[a.status] - statusOrder[b.status];
			if (statusDiff !== 0) return statusDiff;
			return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
		});
	}, [myPlans, offersByPlan, apiOffersByPlanId, currentUserId]);

	// Filtered offers for offers tab
	const filteredOffers = useMemo(() => {
		if (activeTab !== 'offers') return [];
		
		const normalizedSearch = offerSearchTerm.trim().toLowerCase();
		const minMid = minMidBudget ? Number(minMidBudget) : undefined;
		const maxMax = maxMaxBudget ? Number(maxMaxBudget) : undefined;

		return myOffers.filter((item) => {
			const offer = item as Offer & { plan: PublicPlan };
			
			const matchesStatus =
				selectedOfferStatuses.length === 0 || selectedOfferStatuses.includes(offer.status);

			const matchesBudget =
				(minMid === undefined || offer.bands.mid >= minMid) &&
				(maxMax === undefined || offer.bands.max <= maxMax);

			const matchesSearch =
				!normalizedSearch ||
				offer.gardenerName.toLowerCase().includes(normalizedSearch) ||
				offer.plan.title.toLowerCase().includes(normalizedSearch) ||
				offer.plan.city.toLowerCase().includes(normalizedSearch) ||
				offer.message?.toLowerCase().includes(normalizedSearch) ||
				offer.timelineEstimate?.toLowerCase().includes(normalizedSearch);

			return matchesStatus && matchesBudget && matchesSearch;
		});
	}, [myOffers, activeTab, selectedOfferStatuses, minMidBudget, maxMaxBudget, offerSearchTerm]);

	const filteredPlans = useMemo(() => {
		const normalizedSearch = searchTerm.trim().toLowerCase();

		const filtered = myPlans.filter((plan) => {
			const matchesStatus =
				selectedStatuses.length === 0 || selectedStatuses.includes(plan.status);
			const matchesStyle =
				selectedStyles.length === 0 || selectedStyles.includes(plan.styleKey);
			const matchesCity =
				selectedCities.length === 0 || selectedCities.includes(plan.city);

			const offerCount = offersByPlan[plan.id]?.length ?? 0;
			const matchesOffers =
				hasOffersFilter === 'any' ||
				(hasOffersFilter === 'with' ? offerCount > 0 : offerCount === 0);

			const hasPhoto = Boolean(plan.photoBeforeUrl?.trim());
			const matchesPhoto =
				photoFilter === 'any' ||
				(photoFilter === 'with' ? hasPhoto : !hasPhoto);

			const hasAI = Boolean(plan.aiGenerated?.photoAfterUrl?.trim());
			const matchesAI =
				aiFilter === 'any' ||
				(aiFilter === 'with' ? hasAI : !hasAI);

			if (!normalizedSearch) {
				return matchesStatus && matchesStyle && matchesCity && matchesOffers && matchesPhoto && matchesAI;
			}

			const styleTitle = stylePresetsByKey[plan.styleKey]?.title ?? '';

			const matchesSearch =
				plan.title.toLowerCase().includes(normalizedSearch) ||
				plan.city.toLowerCase().includes(normalizedSearch) ||
				styleTitle.toLowerCase().includes(normalizedSearch);

			return matchesStatus && matchesStyle && matchesCity && matchesOffers && matchesPhoto && matchesAI && matchesSearch;
		});

		// Apply sorting (same options as marketplace, minus distance)
		const sorted = [...filtered].sort((a, b) => {
			switch (sortBy) {
				case 'newest':
					return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
				case 'oldest':
					return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
				case 'city':
					return a.city.localeCompare(b.city);
				case 'style': {
					const styleA = stylePresetsByKey[a.styleKey]?.title ?? '';
					const styleB = stylePresetsByKey[b.styleKey]?.title ?? '';
					return styleA.localeCompare(styleB);
				}
				default:
					return 0;
			}
		});

		return sorted;
	}, [myPlans, searchTerm, selectedStatuses, selectedStyles, selectedCities, hasOffersFilter, photoFilter, aiFilter, sortBy, offersByPlan]);

	const styleOptions = useMemo(() => Object.entries(stylePresetsByKey), []);
	const cityOptions = useMemo(
		() => Array.from(new Set(myPlans.map((plan) => plan.city))).sort(),
		[myPlans]
	);

	const activeFiltersCount = useMemo(() => {
		if (activeTab === 'offers') {
			let count = 0;
			count += selectedOfferStatuses.length;
			if (minMidBudget) count += 1;
			if (maxMaxBudget) count += 1;
			return count;
		}
		let count = 0;
		count += selectedStatuses.length;
		count += selectedStyles.length;
		count += selectedCities.length;
		if (hasOffersFilter !== 'any') count += 1;
		if (photoFilter !== 'any') count += 1;
		if (aiFilter !== 'any') count += 1;
		return count;
	}, [activeTab, selectedStatuses, selectedStyles, selectedCities, hasOffersFilter, photoFilter, aiFilter, selectedOfferStatuses, minMidBudget, maxMaxBudget]);

	const toggleSelection = <T,>(value: T, collection: T[], setCollection: (items: T[]) => void) => {
		setCollection(
			collection.includes(value)
				? collection.filter((item) => item !== value)
				: [...collection, value]
		);
	};

	const toggleOfferExpansion = (offerId: string) => {
		setExpandedOfferIds((prev) => {
			const next = new Set(prev);
			if (next.has(offerId)) {
				next.delete(offerId);
			} else {
				next.add(offerId);
			}
			return next;
		});
	};

	const handleResetFilters = () => {
		if (activeTab === 'offers') {
			setSelectedOfferStatuses([]);
			setMinMidBudget('');
			setMaxMaxBudget('');
			setOfferSearchTerm('');
		} else {
			setSelectedStatuses([]);
			setSelectedStyles([]);
			setSelectedCities([]);
			setHasOffersFilter('any');
			setPhotoFilter('any');
			setAIFilter('any');
			setSearchTerm('');
		}
	};

	const handleDeletePlan = async (planId: string, planTitle: string) => {
		if (window.confirm(copy(`Are you sure you want to delete "${planTitle}"? This action cannot be undone.`, `Möchten Sie "${planTitle}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`, `Jeste li sigurni da želite izbrisati "${planTitle}"? Ova radnja se ne može poništiti.`))) {
			try {
				await deletePublicPlan(planId);
				// If this was the plan being edited, clear the editing state and draft
				const editingId = localStorage.getItem('editingPlanId');
				if (editingId === planId) {
					localStorage.removeItem('editingPlanId');
					localStorage.removeItem('gardenQuestionnaireDraft');
				}
				showAlert(copy(`Plan "${planTitle}" has been deleted.`, `Plan "${planTitle}" wurde gelöscht.`, `Plan "${planTitle}" je izbrisan.`), { title: copy('Plan deleted', 'Plan gelöscht', 'Plan izbrisan') });
			} catch (err) {
				console.error('Failed to delete plan', err);
				showAlert(copy('Failed to delete the plan. Please try again.', 'Der Plan konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.', 'Plan nije moguće izbrisati. Pokušajte ponovno.'), { title: copy('Error', 'Fehler', 'Greška') });
			}
		}
	};

	const handleEditPlan = (plan: PublicPlan) => {
		if (plan.canEdit === false) {
			showAlert(copy('This draft is shared read-only by the company.', 'Dieser Entwurf wurde von der Firma nur zum Lesen freigegeben.', 'Ovaj nacrt tvrtka dijeli samo za čitanje.'), { title: copy('Editing not allowed', 'Bearbeitung nicht erlaubt', 'Uređivanje nije dopušteno') });
			return;
		}
		// If it's an AI-generated plan, route to questionnaire with plan data
		if (plan.questionnaire) {
			// Set the editing plan ID - the component will load the plan's data
			localStorage.setItem('editingPlanId', plan.id);
			// Clear any generic draft - we're loading from a specific plan
			localStorage.removeItem('gardenQuestionnaireDraft');
			navigate('/app/seeker/create-plan');
		} else {
			// For legacy plans without questionnaire data, editing will be added later
			showAlert(copy('Editing older plans is coming soon.', 'Das Bearbeiten älterer Pläne kommt bald.', 'Uređivanje starijih planova uskoro dolazi.'), { title: copy('Coming soon', 'Demnächst verfügbar', 'Uskoro dostupno') });
		}
	};

	const handleOfferAction = async (offer: Offer, action: 'accept' | 'reject') => {
		try {
			const offerIdNum = parseInt(offer.id, 10);
			if (!isNaN(offerIdNum) && planOwnerUserIdNum != null) {
				if (action === 'accept') {
					await acceptOfferAPI(offerIdNum);
				} else {
					await rejectOfferAPI(offerIdNum);
				}
				const userIdNum = planOwnerUserIdNum;
				if (!isNaN(userIdNum)) {
					const list = await getOffersForSeeker(userIdNum);
					const byPlan: Record<string, Offer[]> = {};
					list.forEach((backendOffer) => {
						const apiPlanId = `db-${backendOffer.plan_id}`;
						if (!byPlan[apiPlanId]) byPlan[apiPlanId] = [];
						byPlan[apiPlanId].push(mapBackendOfferToFrontend(backendOffer));
					});
					setApiOffersByPlanId(byPlan);
				}
				return;
			}
			// Local fallback for non-API/demo plans
			if (action === 'accept') {
				acceptOfferLocal(offer.id);
			} else {
				rejectOfferLocal(offer.id);
			}
			setOffers(getOffers());
		} catch (err) {
			console.error(`Failed to ${action} offer`, err);
			showAlert(`Failed to ${action} this offer. Please try again.`, { title: 'Offer update failed' });
		}
	};

	const openMessageModal = (offerId: string) => {
		window.dispatchEvent(new Event('app:clear-search-focus'));
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		document.querySelectorAll<HTMLInputElement>('input[type="search"]').forEach((input) => input.blur());
		setTimeout(() => {
			if (document.activeElement instanceof HTMLElement) {
				document.activeElement.blur();
			}
			document.querySelectorAll<HTMLInputElement>('input[type="search"]').forEach((input) => input.blur());
		}, 0);
		setMessageOfferId(offerId);
	};

	// Counts for tab badges (All Plans, Active, Drafts, Closed, Offers)
	const overviewStats = useMemo(() => {
		const base = plans.filter(p => {
			if (p.seekerUserId !== currentUserId) return false;
			if (isPlanDeleted(p.id, p.databaseId)) return false;
			if (p.moderationStatus === 'hidden' || p.moderationStatus === 'deleted') return false;
			return true;
		});
		const totalPlans = base.length;
		const activePlans = base.filter(p => p.status === 'active').length;
		const draftPlans = base.filter(p => p.status === 'draft').length;
		const closedPlans = base.filter(p => p.status === 'closed').length;
		const totalOffers = myOffers.length;
		return { totalPlans, activePlans, draftPlans, closedPlans, totalOffers };
	}, [plans, currentUserId, myOffers.length]);

	// Choose a single active invite to highlight (we show only one banner at a time).
	// If the shared plan is already accepted in the loaded plan list, suppress the banner
	// even if an older pending invite row still exists in the database.
	const activeInvite: SeekerPlanInviteSummary | null = useMemo(() => {
		const unresolvedInvite = pendingInvites.find((invite) => {
			const matchingPlan = plans.find((plan) => {
				if (invite.plan_id != null && plan.databaseId === invite.plan_id) return true;
				if (invite.code && plan.inviteCode === invite.code) return true;
				return false;
			});
			if (!matchingPlan) return true;
			return matchingPlan.inviteStatus !== 'used';
		});
		if (unresolvedInvite) return unresolvedInvite;
		// If there is an invite code in the URL but we don't have a matching email-based invite,
		// we still want to hint that they should use their code by sending them to Create Plan.
		return null;
	}, [pendingInvites, plans]);

	const inviteLinkedPlan: PublicPlan | null = useMemo(() => {
		const code = activeInvite?.code || inviteCodeFromUrl;
		if (!code) return null;
		return (
			plans.find((p) => p.inviteCode === code && p.databaseId != null) ||
			null
		);
	}, [plans, activeInvite, inviteCodeFromUrl]);

	const handleUseInvite = () => {
		const code = activeInvite?.code || inviteCodeFromUrl;
		const offerQuery = offerIdFromUrl ? `&offer_id=${encodeURIComponent(offerIdFromUrl)}` : '';
		if (inviteLinkedPlan?.databaseId) {
			const base = `/app/seeker/plan/db-${inviteLinkedPlan.databaseId}`;
			const inviteParam = encodeURIComponent(code || inviteLinkedPlan.inviteCode || '');
			navigate(
				offerIdFromUrl
					? `${base}/offers?invite=${inviteParam}${offerQuery}`
					: `${base}?invite=${inviteParam}`
			);
			return;
		}
		if (activeInvite?.plan_id) {
			const base = `/app/seeker/plan/db-${activeInvite.plan_id}`;
			navigate(offerIdFromUrl ? `${base}/offers?invite=${encodeURIComponent(code || '')}${offerQuery}` : `${base}?invite=${encodeURIComponent(code || '')}`);
			return;
		}
		if (!code) {
			navigate('/app/seeker/create-plan');
			return;
		}
		// Preserve any existing query params but ensure invite is set, so Create Plan / Questionnaire can pick it up.
		navigate(`/app/seeker/create-plan?invite=${encodeURIComponent(code)}`);
	};

	const handleDeclineInvite = async () => {
		if (!activeInvite) return;
		const userIdNum = planOwnerUserIdNum;
		if (userIdNum == null || Number.isNaN(userIdNum)) {
			setInviteError('You must be logged in as the invited seeker to decline an invite.');
			return;
		}
		if (!window.confirm(`Are you sure you want to decline the invite from ${activeInvite.company_name}? This code cannot be used again.`)) {
			return;
		}
		try {
			await declinePlanInvite(activeInvite.id, userIdNum);
			setPendingInvites((prev) => prev.filter((inv) => inv.id !== activeInvite.id));
			setInviteError(null);
			// Remove invite from URL so the banner disappears (inviteCodeFromUrl drives banner when no pendingInvites)
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				next.delete('invite');
				next.delete('code');
				return next;
			}, { replace: true });
		} catch (err) {
			console.error('Failed to decline invite', err);
			setInviteError('We could not decline this invitation right now. Please try again.');
		}
	};

	return (
		<>
			<div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-md-between gap-3 mb-4">
				<div>
					<h1 className="h3 mb-1">{copy('My Garden Plans', 'Meine Gartenpläne', 'Moji vrtni planovi')}</h1>
					<p className="text-muted small mb-0">
						{overviewStats.totalPlans === 0
							? copy('Start by creating your first garden plan – we’ll guide you step by step.', 'Erstellen Sie zuerst Ihren ersten Gartenplan – wir führen Sie Schritt für Schritt.', 'Započnite izradom svog prvog vrtnog plana – vodit ćemo vas korak po korak.')
							: copy('Manage your garden plans and review offers from gardeners.', 'Verwalten Sie Ihre Gartenpläne und prüfen Sie Angebote von Gärtnern.', 'Upravljajte svojim vrtnim planovima i pregledajte ponude vrtlara.')}
					</p>
				</div>
				<div className="d-flex flex-column flex-sm-row flex-wrap align-items-stretch align-items-sm-center gap-2 justify-content-sm-end w-100 w-md-auto">
					<input
						type="search"
						className="form-control myplans-toolbar-search w-100"
						placeholder={activeTab === 'offers'
							? copy('Search by gardener, plan, city, notes...', 'Suche nach Gärtner, Plan, Stadt, Notizen...', 'Pretraži po vrtlaru, planu, gradu, bilješkama...')
							: copy('Search by title, city, or style...', 'Suche nach Titel, Stadt oder Stil...', 'Pretraži po naslovu, gradu ili stilu...')}
						value={activeTab === 'offers' ? offerSearchTerm : searchTerm}
						onChange={(event) => {
							if (activeTab === 'offers') {
								setOfferSearchTerm(event.target.value);
							} else {
								setSearchTerm(event.target.value);
							}
						}}
					/>
					{activeTab !== 'offers' && (
						<>
							<label className="text-muted small mb-0">{copy('Sort:', 'Sortieren:', 'Sortiraj:')}</label>
							<select
								className="form-select form-select-sm"
								style={{ width: 'auto', minWidth: '120px' }}
								value={sortBy}
								onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
							>
								<option value="newest">{copy('Newest First', 'Neueste zuerst', 'Najnovije prvo')}</option>
								<option value="oldest">{copy('Oldest First', 'Älteste zuerst', 'Najstarije prvo')}</option>
								<option value="city">{copy('By City', 'Nach Stadt', 'Po gradu')}</option>
								<option value="style">{copy('By Style', 'Nach Stil', 'Po stilu')}</option>
							</select>
						</>
					)}
					<FilterToggle
						isOpen={isFiltersOpen}
						onToggle={() => setIsFiltersOpen((prev) => !prev)}
						activeCount={activeFiltersCount}
					/>
				</div>
			</div>

			{/* Company invite banner – shown only while an invite is still unresolved */}
			{user?.role === 'seeker' && (activeInvite || (inviteCodeFromUrl && !hasClaimedInviteFromUrl)) && (
				<div className="alert alert-success d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4" role="status">
					<div>
						<div className="fw-semibold mb-1">
							{activeInvite?.company_name ? (
								<>
									{(inviteLinkedPlan || activeInvite.plan_id)
										? <>{copy("You've been invited by", 'Sie wurden eingeladen von', 'Pozvani ste od')} <span>{activeInvite.company_name}</span> {copy('to review a shared garden plan.', 'einen geteilten Gartenplan zu prüfen.', 'da biste pregledali zajednički vrtni plan.')}</>
										: <>{copy("You've been invited by", 'Sie wurden eingeladen von', 'Pozvani ste od')} <span>{activeInvite.company_name}</span> {copy('to create a free garden plan.', 'einen kostenlosen Gartenplan zu erstellen.', 'da biste izradili besplatan vrtni plan.')}</>}
								</>
							) : (
								<>{inviteLinkedPlan ? copy('You have a shared plan invite code.', 'Sie haben einen Einladungscode für einen geteilten Plan.', 'Imate kod pozivnice za dijeljeni plan.') : copy('You have a company invite code you can use to create a free garden plan.', 'Sie haben einen Firmen-Einladungscode, mit dem Sie kostenlos einen Gartenplan erstellen können.', 'Imate kod pozivnice tvrtke koji možete koristiti za izradu besplatnog vrtnog plana.')}</>
							)}
						</div>
						<p className="mb-0 small">
							{(inviteLinkedPlan || activeInvite?.plan_id)
								? copy('Open the shared plan, then accept or reject the invite.', 'Öffnen Sie den geteilten Plan und akzeptieren oder lehnen Sie dann die Einladung ab.', 'Otvorite dijeljeni plan, zatim prihvatite ili odbijte pozivnicu.')
								: copy('This plan will go directly to the company and won’t appear in the public marketplace.', 'Dieser Plan geht direkt an die Firma und erscheint nicht im öffentlichen Marktplatz.', 'Ovaj plan ide izravno tvrtki i neće se pojaviti na javnoj tržnici.')}
						</p>
						{inviteError && (
							<p className="mb-0 mt-1 small text-danger">
								{inviteError}
							</p>
						)}
					</div>
					<div className="d-flex flex-wrap gap-2">
						<button
							type="button"
							className="btn btn-success btn-sm"
							onClick={handleUseInvite}
							disabled={isLoadingInvites}
						>
							{(inviteLinkedPlan || activeInvite?.plan_id) ? copy('View shared plan', 'Geteilten Plan ansehen', 'Prikaži dijeljeni plan') : copy('Use invite to create plan', 'Einladung zum Erstellen eines Plans nutzen', 'Iskoristi pozivnicu za izradu plana')}
						</button>
						{activeInvite && (
							<button
								type="button"
								className="btn btn-outline-secondary btn-sm"
								onClick={handleDeclineInvite}
								disabled={isLoadingInvites}
							>
								{copy('No thanks', 'Nein danke', 'Ne, hvala')}
							</button>
						)}
					</div>
				</div>
			)}

			{/* Tabs with counts */}
			<div className="mb-4 myplans-tabs-scroll">
				<ul className="nav nav-tabs border-0 gap-2 mb-0" style={{ borderBottom: 'none' }}>
					<li className="nav-item">
						<button
							className="nav-link rounded-3 border-0 d-inline-flex align-items-center gap-2 px-4 py-3"
							onClick={() => setSearchParams({})}
							type="button"
							style={{
								color: activeTab === 'all' ? '#fff' : '#5a6c5d',
								backgroundColor: activeTab === 'all' ? '#2f9e59' : '#f0f4f0',
								fontWeight: activeTab === 'all' ? '600' : '500',
								fontSize: '1rem'
							}}
							onMouseEnter={(e) => {
								if (activeTab !== 'all') {
									e.currentTarget.style.backgroundColor = '#e2ebe3';
									e.currentTarget.style.color = '#2d4a35';
								}
							}}
							onMouseLeave={(e) => {
								if (activeTab !== 'all') {
									e.currentTarget.style.backgroundColor = '#f0f4f0';
									e.currentTarget.style.color = '#5a6c5d';
								}
							}}
						>
							{copy('All Plans', 'Alle Pläne', 'Svi planovi')}
							<span
								className="badge rounded-pill"
								style={{
									backgroundColor: activeTab === 'all' ? 'rgba(255,255,255,0.3)' : 'rgba(90,108,93,0.2)',
									color: 'inherit',
									fontSize: '0.85rem'
								}}
							>
								{overviewStats.totalPlans}
							</span>
						</button>
					</li>
					<li className="nav-item">
						<button
							className="nav-link rounded-3 border-0 d-inline-flex align-items-center gap-2 px-4 py-3"
							onClick={() => setSearchParams({ tab: 'active' })}
							type="button"
							style={{
								color: activeTab === 'active' ? '#fff' : '#5a6c5d',
								backgroundColor: activeTab === 'active' ? '#2f9e59' : '#f0f4f0',
								fontWeight: activeTab === 'active' ? '600' : '500',
								fontSize: '1rem'
							}}
							onMouseEnter={(e) => {
								if (activeTab !== 'active') {
									e.currentTarget.style.backgroundColor = '#e2ebe3';
									e.currentTarget.style.color = '#2d4a35';
								}
							}}
							onMouseLeave={(e) => {
								if (activeTab !== 'active') {
									e.currentTarget.style.backgroundColor = '#f0f4f0';
									e.currentTarget.style.color = '#5a6c5d';
								}
							}}
						>
							{copy('Active', 'Aktiv', 'Aktivni')}
							<span
								className="badge rounded-pill"
								style={{
									backgroundColor: activeTab === 'active' ? 'rgba(255,255,255,0.3)' : 'rgba(90,108,93,0.2)',
									color: 'inherit',
									fontSize: '0.85rem'
								}}
							>
								{overviewStats.activePlans}
							</span>
						</button>
					</li>
					<li className="nav-item">
						<button
							className="nav-link rounded-3 border-0 d-inline-flex align-items-center gap-2 px-4 py-3"
							onClick={() => setSearchParams({ tab: 'drafts' })}
							type="button"
							style={{
								color: activeTab === 'drafts' ? '#fff' : '#5a6c5d',
								backgroundColor: activeTab === 'drafts' ? '#2f9e59' : '#f0f4f0',
								fontWeight: activeTab === 'drafts' ? '600' : '500',
								fontSize: '1rem'
							}}
							onMouseEnter={(e) => {
								if (activeTab !== 'drafts') {
									e.currentTarget.style.backgroundColor = '#e2ebe3';
									e.currentTarget.style.color = '#2d4a35';
								}
							}}
							onMouseLeave={(e) => {
								if (activeTab !== 'drafts') {
									e.currentTarget.style.backgroundColor = '#f0f4f0';
									e.currentTarget.style.color = '#5a6c5d';
								}
							}}
						>
							{copy('Drafts', 'Entwürfe', 'Skice')}
							<span
								className="badge rounded-pill"
								style={{
									backgroundColor: activeTab === 'drafts' ? 'rgba(255,255,255,0.3)' : 'rgba(90,108,93,0.2)',
									color: 'inherit',
									fontSize: '0.85rem'
								}}
							>
								{overviewStats.draftPlans}
							</span>
						</button>
					</li>
					<li className="nav-item">
						<button
							className="nav-link rounded-3 border-0 d-inline-flex align-items-center gap-2 px-4 py-3"
							onClick={() => setSearchParams({ tab: 'closed' })}
							type="button"
							style={{
								color: activeTab === 'closed' ? '#fff' : '#5a6c5d',
								backgroundColor: activeTab === 'closed' ? '#2f9e59' : '#f0f4f0',
								fontWeight: activeTab === 'closed' ? '600' : '500',
								fontSize: '1rem'
							}}
							onMouseEnter={(e) => {
								if (activeTab !== 'closed') {
									e.currentTarget.style.backgroundColor = '#e2ebe3';
									e.currentTarget.style.color = '#2d4a35';
								}
							}}
							onMouseLeave={(e) => {
								if (activeTab !== 'closed') {
									e.currentTarget.style.backgroundColor = '#f0f4f0';
									e.currentTarget.style.color = '#5a6c5d';
								}
							}}
						>
							{copy('Closed', 'Geschlossen', 'Zatvoreno')}
							<span
								className="badge rounded-pill"
								style={{
									backgroundColor: activeTab === 'closed' ? 'rgba(255,255,255,0.3)' : 'rgba(90,108,93,0.2)',
									color: 'inherit',
									fontSize: '0.85rem'
								}}
							>
								{overviewStats.closedPlans}
							</span>
						</button>
					</li>
					<li className="nav-item">
						<button
							className="nav-link rounded-3 border-0 d-inline-flex align-items-center gap-2 px-4 py-3"
							onClick={() => setSearchParams({ tab: 'offers' })}
							type="button"
							style={{
								color: activeTab === 'offers' ? '#fff' : '#5a6c5d',
								backgroundColor: activeTab === 'offers' ? '#2f9e59' : '#f0f4f0',
								fontWeight: activeTab === 'offers' ? '600' : '500',
								fontSize: '1rem'
							}}
							onMouseEnter={(e) => {
								if (activeTab !== 'offers') {
									e.currentTarget.style.backgroundColor = '#e2ebe3';
									e.currentTarget.style.color = '#2d4a35';
								}
							}}
							onMouseLeave={(e) => {
								if (activeTab !== 'offers') {
									e.currentTarget.style.backgroundColor = '#f0f4f0';
									e.currentTarget.style.color = '#5a6c5d';
								}
							}}
						>
							{copy('Offers', 'Angebote', 'Ponude')}
							<span
								className="badge rounded-pill"
								style={{
									backgroundColor: activeTab === 'offers' ? 'rgba(255,255,255,0.3)' : 'rgba(90,108,93,0.2)',
									color: 'inherit',
									fontSize: '0.85rem'
								}}
							>
								{overviewStats.totalOffers}
							</span>
						</button>
					</li>
				</ul>
			</div>

			{/* Active filter chips (plans tabs only, same pattern as marketplace) */}
			{activeTab !== 'offers' && activeFiltersCount > 0 && (
				<div className="d-flex flex-wrap align-items-center gap-2 mb-4">
					{selectedStatuses.length > 0 && selectedStatuses.map((status) => (
						<span
							key={status}
							className="badge text-bg-light text-dark d-flex align-items-center gap-1"
							style={{ cursor: 'pointer', fontSize: '0.875rem' }}
							onClick={() => setSelectedStatuses(selectedStatuses.filter((s) => s !== status))}
						>
							{localizePlanStatus(status)}
							<i className="bi bi-x"></i>
						</span>
					))}
					{selectedStyles.length > 0 && selectedStyles.map((styleKey) => (
						<span
							key={styleKey}
							className="badge text-bg-light text-dark d-flex align-items-center gap-1"
							style={{ cursor: 'pointer', fontSize: '0.875rem' }}
							onClick={() => setSelectedStyles(selectedStyles.filter((s) => s !== styleKey))}
						>
							{stylePresetsByKey[styleKey]?.title ?? styleKey}
							<i className="bi bi-x"></i>
						</span>
					))}
					{selectedCities.length > 0 && selectedCities.map((city) => (
						<span
							key={city}
							className="badge text-bg-light text-dark d-flex align-items-center gap-1"
							style={{ cursor: 'pointer', fontSize: '0.875rem' }}
							onClick={() => setSelectedCities(selectedCities.filter((c) => c !== city))}
						>
							<i className="bi bi-geo-alt-fill"></i>
							{city}
							<i className="bi bi-x"></i>
						</span>
					))}
					{hasOffersFilter !== 'any' && (
						<span
							className="badge text-bg-light text-dark d-flex align-items-center gap-1"
							style={{ cursor: 'pointer', fontSize: '0.875rem' }}
							onClick={() => setHasOffersFilter('any')}
						>
							{hasOffersFilter === 'with' ? copy('Has offers', 'Hat Angebote', 'Ima ponude') : copy('No offers yet', 'Noch keine Angebote', 'Još nema ponuda')}
							<i className="bi bi-x"></i>
						</span>
					)}
					{photoFilter !== 'any' && (
						<span
							className="badge text-bg-light text-dark d-flex align-items-center gap-1"
							style={{ cursor: 'pointer', fontSize: '0.875rem' }}
							onClick={() => setPhotoFilter('any')}
						>
							{photoFilter === 'with' ? copy('With photos', 'Mit Fotos', 'S fotografijama') : copy('Without photos', 'Ohne Fotos', 'Bez fotografija')}
							<i className="bi bi-x"></i>
						</span>
					)}
					{aiFilter !== 'any' && (
						<span
							className="badge text-bg-light text-dark d-flex align-items-center gap-1"
							style={{ cursor: 'pointer', fontSize: '0.875rem' }}
							onClick={() => setAIFilter('any')}
						>
							{aiFilter === 'with' ? copy('Design ready', 'Design bereit', 'Dizajn spreman') : copy('No design image', 'Kein Designbild', 'Nema slike dizajna')}
							<i className="bi bi-x"></i>
						</span>
					)}
					<button
						type="button"
						className="btn btn-sm btn-link text-muted p-0"
						onClick={handleResetFilters}
						style={{ fontSize: '0.875rem' }}
					>
						{copy('Clear all', 'Alles zurücksetzen', 'Očisti sve')}
					</button>
				</div>
			)}

			{myPlans.length === 0 && activeTab === 'all' && (
				<div className="card border-0 shadow-sm position-relative overflow-hidden">
					<div
						className="position-absolute top-0 start-0 w-100 h-100"
						style={{
							background: 'radial-gradient(circle at top left, rgba(47,158,89,0.12), transparent 55%), radial-gradient(circle at bottom right, rgba(255,255,255,0.9), transparent 60%)',
							pointerEvents: 'none'
						}}
					/>
					<div className="card-body py-5 px-4 px-lg-5 position-relative">
						<div className="row g-4 align-items-center">
							<div className="col-12 col-lg-7">
								<div className="d-inline-flex align-items-center mb-2 px-3 py-1 rounded-pill bg-success-subtle text-success small fw-semibold">
									<i className="bi bi-house-heart me-2"></i>
									For garden seekers
								</div>
								<h2 className="h3 mb-2 text-success-emphasis">
									Welcome{seekerFirstName ? `, ${seekerFirstName}` : ''} to HortEra
								</h2>
								<p className="text-muted mb-4">
									Let’s turn your garden into a place you love. Create a plan, describe your space,
									and we’ll help you connect with gardeners for quotes and ideas.
								</p>
								<ol className="list-unstyled mb-4">
									<li className="d-flex mb-3">
										<div className="badge bg-success-subtle text-success me-3 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>1</div>
										<div>
											<div className="fw-semibold">Create your garden plan</div>
											<div className="text-muted small">Answer a few questions about your garden, style, and budget.</div>
										</div>
									</li>
									<li className="d-flex mb-3">
										<div className="badge bg-success-subtle text-success me-3 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>2</div>
										<div>
											<div className="fw-semibold">Receive offers from gardeners</div>
											<div className="text-muted small">Compare budgets, timelines, and messages in one place.</div>
										</div>
									</li>
									<li className="d-flex">
										<div className="badge bg-success-subtle text-success me-3 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>3</div>
										<div>
											<div className="fw-semibold">Turn a plan into a project</div>
											<div className="text-muted small">Accept an offer and track progress under “My Projects”.</div>
										</div>
									</li>
								</ol>
								<div className="d-flex flex-wrap gap-3">
									<Link
										to="/app/seeker/create-plan"
										className="btn btn-success btn-lg px-4"
										onClick={() => {
											localStorage.removeItem('editingPlanId');
											localStorage.removeItem('gardenQuestionnaireDraft');
										}}
									>
										<i className="bi bi-plus-circle me-1"></i>
										Create my first plan
									</Link>
									<Link to="/app/seeker/projects" className="btn btn-outline-secondary btn-lg px-4">
										View my projects
									</Link>
									<Link to="/" className="btn btn-link text-muted">
										How HortEra works
									</Link>
								</div>
							</div>
							<div className="col-12 col-lg-5">
								<div
									className="rounded-4 p-4 h-100 d-flex flex-column justify-content-between"
									style={{
										background: 'linear-gradient(145deg, #e9f7ef 0%, #fdfcfb 60%, #f0f9f4 100%)',
										boxShadow: '0 18px 45px rgba(47, 158, 89, 0.08)'
									}}
								>
									<div className="mb-4">
										<h3 className="h6 fw-semibold mb-2 d-flex align-items-center">
											<i className="bi bi-lightbulb-fill text-warning me-2"></i>
											Imagine your before & after
										</h3>
										<p className="text-muted small mb-0">
											You’ll be able to keep your ideas, photos, and offers for this garden in one place,
											so it’s easy to come back and refine your plan over time.
										</p>
									</div>
									<div className="mt-2 small text-muted">
										<span className="badge bg-success-subtle text-success me-2">
											My plans
										</span>
										<span>is where you’ll always find this garden again.</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{myPlans.length === 0 && activeTab === 'active' && (
				<div className="card">
					<div className="card-body text-center py-5">
						<p className="mb-3 text-muted">{copy("You don't have any active (published) plans yet.", 'Sie haben noch keine aktiven (veröffentlichten) Pläne.', 'Još nemate nijedan aktivan (objavljen) plan.')}</p>
						<p className="mb-0 small text-muted">{copy('Publish a draft to make it visible to gardeners, or create a new plan.', 'Veröffentlichen Sie einen Entwurf, damit er für Gärtner sichtbar ist, oder erstellen Sie einen neuen Plan.', 'Objavite skicu kako bi bila vidljiva vrtlarima ili kreirajte novi plan.')}</p>
					</div>
				</div>
			)}

			{myPlans.length === 0 && activeTab === 'drafts' && (
				<div className="card">
					<div className="card-body text-center py-5">
						<p className="mb-3 text-muted">{copy("You don't have any draft plans yet.", 'Sie haben noch keine Entwurfspläne.', 'Još nemate nijednu skicu plana.')}</p>
						<Link 
							to="/app/seeker/create-plan" 
							className="btn btn-success"
							onClick={() => {
								// Clear editingPlanId to ensure we start with a fresh form
								localStorage.removeItem('editingPlanId');
								localStorage.removeItem('gardenQuestionnaireDraft');
							}}
						>
							{copy('Create a new plan', 'Neuen Plan erstellen', 'Kreiraj novi plan')}
						</Link>
					</div>
				</div>
			)}

			{myPlans.length === 0 && activeTab === 'closed' && (
				<div className="card">
					<div className="card-body text-center py-5">
						<p className="mb-3 text-muted">{copy("You don't have any closed plans yet.", 'Sie haben noch keine geschlossenen Pläne.', 'Još nemate nijedan zatvoren plan.')}</p>
						<p className="mb-0 small text-muted">{copy('Plans you close (e.g. after accepting an offer) will appear here.', 'Pläne, die Sie schließen (z. B. nach Annahme eines Angebots), erscheinen hier.', 'Planovi koje zatvorite (npr. nakon prihvaćanja ponude) pojavit će se ovdje.')}</p>
					</div>
				</div>
			)}

			{myOffers.length === 0 && activeTab === 'offers' && (
				<div className="card">
					<div className="card-body text-center py-5">
						<p className="mb-3 text-muted">{copy("You don't have any offers yet.", 'Sie haben noch keine Angebote.', 'Još nemate nijednu ponudu.')}</p>
						<p className="small text-muted mb-0">{copy('Offers will appear here once gardeners submit quotes for your plans.', 'Angebote erscheinen hier, sobald Gärtner Angebote für Ihre Pläne einreichen.', 'Ponude će se ovdje pojaviti kada vrtlari pošalju ponude za vaše planove.')}</p>
					</div>
				</div>
			)}

			<FiltersPanel
				isOpen={isFiltersOpen}
				onApply={() => setIsFiltersOpen(false)}
				onReset={handleResetFilters}
			>
				{activeTab === 'offers' ? (
					<div className="row g-4">
						<div className="col-12 col-lg-3">
							<div className="mb-2 text-muted text-uppercase small">{copy('Status', 'Status', 'Status')}</div>
							{(['pending', 'accepted', 'rejected'] as const).map((status) => (
								<div className="form-check" key={status}>
									<input
										className="form-check-input"
										type="checkbox"
										id={`offer-status-${status}`}
										checked={selectedOfferStatuses.includes(status)}
										onChange={() => toggleSelection(status, selectedOfferStatuses, setSelectedOfferStatuses)}
									/>
									<label className="form-check-label" htmlFor={`offer-status-${status}`}>
										{localizeOfferStatus(status)}
									</label>
								</div>
							))}
						</div>
						<div className="col-12 col-lg-3">
							<div className="mb-2 text-muted text-uppercase small">{copy('Budget bands (€)', 'Budgetspannen (€)', 'Rasponi budžeta (€)')}</div>
							<div className="input-group mb-2">
								<span className="input-group-text">{copy('Mid', 'Srednje', 'Srednje')} ≥</span>
								<input
									type="number"
									min={0}
									className="form-control"
									value={minMidBudget}
									onChange={(event) => setMinMidBudget(event.target.value)}
									placeholder="0"
								/>
							</div>
							<div className="input-group">
								<span className="input-group-text">{copy('Max', 'Maks', 'Maks')} ≤</span>
								<input
									type="number"
									min={0}
									className="form-control"
									value={maxMaxBudget}
									onChange={(event) => setMaxMaxBudget(event.target.value)}
									placeholder={copy('Any', 'Bilo koji', 'Bilo koji')}
								/>
							</div>
						</div>
					</div>
				) : (
				<div className="row g-4">
					<div className="col-12 col-lg-3">
						<div className="mb-2 text-muted text-uppercase small">{copy('Status', 'Status', 'Status')}</div>
						{(['draft', 'active', 'closed'] as const).map((status) => (
							<div className="form-check" key={status}>
								<input
									className="form-check-input"
									type="checkbox"
									id={`my-plan-status-${status}`}
									checked={selectedStatuses.includes(status)}
									onChange={() => toggleSelection(status, selectedStatuses, setSelectedStatuses)}
								/>
								<label className="form-check-label" htmlFor={`my-plan-status-${status}`}>
									{localizePlanStatus(status)}
								</label>
							</div>
						))}
					</div>
					<div className="col-12 col-lg-3">
						<div className="mb-2 text-muted text-uppercase small">{copy('Style', 'Stil', 'Stil')}</div>
						{styleOptions.map(([key, preset]) => (
							<div className="form-check" key={key}>
								<input
									className="form-check-input"
									type="checkbox"
									id={`my-plan-style-${key}`}
									checked={selectedStyles.includes(key as PublicPlan['styleKey'])}
									onChange={() =>
										toggleSelection(
											key as PublicPlan['styleKey'],
											selectedStyles,
											setSelectedStyles
										)
									}
								/>
								<label className="form-check-label" htmlFor={`my-plan-style-${key}`}>
									{localizeStyleLabel(key)}
								</label>
							</div>
						))}
					</div>
					<div className="col-12 col-lg-3">
						<div className="mb-2 text-muted text-uppercase small">{copy('City', 'Stadt', 'Grad')}</div>
						{cityOptions.map((city) => (
							<div className="form-check" key={city}>
								<input
									className="form-check-input"
									type="checkbox"
									id={`my-plan-city-${city}`}
									checked={selectedCities.includes(city)}
									onChange={() => toggleSelection(city, selectedCities, setSelectedCities)}
								/>
								<label className="form-check-label" htmlFor={`my-plan-city-${city}`}>
									{city}
								</label>
							</div>
						))}
						{cityOptions.length === 0 && (
							<p className="text-muted small mb-0">{copy('Cities appear once you add plans.', 'Städte erscheinen, sobald Sie Pläne hinzufügen.', 'Gradovi se pojavljuju kada dodate planove.')}</p>
						)}
					</div>
					<div className="col-12 col-lg-3">
						<div className="mb-2 text-muted text-uppercase small">{copy('Offers', 'Angebote', 'Ponude')}</div>
						<div className="form-check">
							<input
								className="form-check-input"
								type="radio"
								name="my-plan-offers-filter"
								id="my-plan-offers-any"
								checked={hasOffersFilter === 'any'}
								onChange={() => setHasOffersFilter('any')}
							/>
							<label className="form-check-label" htmlFor="my-plan-offers-any">
								{copy('Any offer status', 'Beliebiger Angebotsstatus', 'Bilo koji status ponude')}
							</label>
						</div>
						<div className="form-check">
							<input
								className="form-check-input"
								type="radio"
								name="my-plan-offers-filter"
								id="my-plan-offers-with"
								checked={hasOffersFilter === 'with'}
								onChange={() => setHasOffersFilter('with')}
							/>
							<label className="form-check-label" htmlFor="my-plan-offers-with">
								{copy('Has offers', 'Hat Angebote', 'Ima ponude')}
							</label>
						</div>
						<div className="form-check">
							<input
								className="form-check-input"
								type="radio"
								name="my-plan-offers-filter"
								id="my-plan-offers-without"
								checked={hasOffersFilter === 'without'}
								onChange={() => setHasOffersFilter('without')}
							/>
							<label className="form-check-label" htmlFor="my-plan-offers-without">
								{copy('No offers yet', 'Noch keine Angebote', 'Još nema ponuda')}
							</label>
						</div>
					</div>
					<div className="col-12 col-lg-3">
						<div className="mb-2 text-muted text-uppercase small">{copy('Photo', 'Foto', 'Fotografija')}</div>
						<div className="form-check">
							<input className="form-check-input" type="radio" name="my-plan-photo" id="my-plan-photo-any" checked={photoFilter === 'any'} onChange={() => setPhotoFilter('any')} />
							<label className="form-check-label" htmlFor="my-plan-photo-any">{copy('Any', 'Beliebig', 'Bilo koja')}</label>
						</div>
						<div className="form-check">
							<input className="form-check-input" type="radio" name="my-plan-photo" id="my-plan-photo-with" checked={photoFilter === 'with'} onChange={() => setPhotoFilter('with')} />
							<label className="form-check-label" htmlFor="my-plan-photo-with">{copy('With before photo', 'Mit Vorher-Foto', 'S fotografijom prije')}</label>
						</div>
						<div className="form-check">
							<input className="form-check-input" type="radio" name="my-plan-photo" id="my-plan-photo-without" checked={photoFilter === 'without'} onChange={() => setPhotoFilter('without')} />
							<label className="form-check-label" htmlFor="my-plan-photo-without">{copy('Without photo', 'Ohne Foto', 'Bez fotografije')}</label>
						</div>
					</div>
					<div className="col-12 col-lg-3">
						<div className="mb-2 text-muted text-uppercase small">{copy('Design image', 'Designbild', 'Slika dizajna')}</div>
						<div className="form-check">
							<input className="form-check-input" type="radio" name="my-plan-ai" id="my-plan-ai-any" checked={aiFilter === 'any'} onChange={() => setAIFilter('any')} />
							<label className="form-check-label" htmlFor="my-plan-ai-any">{copy('Any', 'Beliebig', 'Bilo koja')}</label>
						</div>
						<div className="form-check">
							<input className="form-check-input" type="radio" name="my-plan-ai" id="my-plan-ai-with" checked={aiFilter === 'with'} onChange={() => setAIFilter('with')} />
							<label className="form-check-label" htmlFor="my-plan-ai-with">{copy('With design image', 'Mit Designbild', 'Sa slikom dizajna')}</label>
						</div>
						<div className="form-check">
							<input className="form-check-input" type="radio" name="my-plan-ai" id="my-plan-ai-without" checked={aiFilter === 'without'} onChange={() => setAIFilter('without')} />
							<label className="form-check-label" htmlFor="my-plan-ai-without">{copy('No design image yet', 'Noch kein Designbild', 'Još nema slike dizajna')}</label>
						</div>
					</div>
				</div>
				)}
			</FiltersPanel>
			
			{/* Offers Tab Content */}
			{activeTab === 'offers' && myOffers.length > 0 && (
				<div className="card">
					<div className="card-body">
						{filteredOffers.length === 0 ? (
							<div className="text-center py-5 text-muted">
								{copy('No offers match your filters yet.', 'Keine Angebote entsprechen Ihren Filtern.', 'Nijedna ponuda ne odgovara vašim filtrima.')}
							</div>
						) : (
							<div className="table-responsive">
								<table className="table align-middle">
									<thead>
										<tr>
											<th>{copy('Plan', 'Plan', 'Plan')}</th>
											<th>{copy('Gardener', 'Gärtner', 'Vrtlar')}</th>
											<th>{copy('Estimated', 'Geschätzt', 'Procijenjeno')}</th>
											<th>{copy('VAT 19%', 'MwSt. 19%', 'PDV 19%')}</th>
											<th>{copy('Notes', 'Notizen', 'Bilješke')}</th>
											<th>{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</th>
											<th>{copy('Created', 'Erstellt', 'Kreirano')}</th>
											<th>{copy('Status', 'Status', 'Status')}</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{filteredOffers.map((item) => {
											const offer = item as Offer & { plan: PublicPlan };
											const isExpanded = expandedOfferIds.has(offer.id);
											return (
												<Fragment key={offer.id}>
													<tr>
														<td>
															<div className="d-flex align-items-start gap-2">
																<button
																	type="button"
																	className="btn btn-sm btn-link p-0 mt-1"
																	onClick={() => toggleOfferExpansion(offer.id)}
																	aria-label={isExpanded ? copy('Collapse offer details', 'Angebotsdetails einklappen', 'Sakrij detalje ponude') : copy('Expand offer details', 'Angebotsdetails ausklappen', 'Prikaži detalje ponude')}
																	style={{ minWidth: 22 }}
																>
																	<i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
																</button>
																<div>
																	<Link
																		to={`/app/seeker/plan/${planDetailId(offer.plan)}`}
																		className="text-decoration-none fw-medium"
																		style={{ color: '#2f9e59' }}
																	>
																		{offer.plan.title}
																	</Link>
																	<div className="small text-muted">
																		<i className="bi bi-geo-alt me-1"></i>{offer.plan.city}
																	</div>
																</div>
															</div>
														</td>
														<td>
															<div className="fw-medium">{offer.gardenerName}</div>
														</td>
														<td>{(offer.bands.mid || offer.bands.min).toLocaleString()} €</td>
														<td>19%</td>
														<td style={{ maxWidth: '200px' }}>
															<span className="small text-muted">{offer.message || copy('—', '—', '—')}</span>
														</td>
														<td style={{ maxWidth: '200px' }}>
															<span className="small">{offer.timelineEstimate || copy('—', '—', '—')}</span>
														</td>
														<td>
															<span className="small">
																{new Date(offer.createdAt).toLocaleDateString(locale)}
															</span>
														</td>
														<td>
															<span className={`badge ${
																offer.status === 'accepted' ? 'text-bg-success' :
																offer.status === 'rejected' ? 'text-bg-danger' :
																'text-bg-warning'
															}`}>
																{localizeOfferStatus(offer.status)}
															</span>
														</td>
														<td className="text-end">
															<div className="btn-group btn-group-sm">
																<button
																	type="button"
																	className="btn btn-outline-primary btn-sm"
																	title={copy('Message gardener', 'Nachricht an den Gärtner', 'Pošalji poruku vrtlaru')}
																	onClick={(e) => {
																		e.preventDefault();
																		openMessageModal(offer.id);
																	}}
																>
																	<i className="bi bi-chat-dots me-1"></i>{copy('Message', 'Nachricht', 'Poruka')}
																</button>
																<Link
																	to={`/app/seeker/plan/${planDetailId(offer.plan)}/offers`}
																	state={{ prefetchedOffers: filteredOffers.filter((item) => (item as Offer & { plan: PublicPlan }).plan.id === offer.plan.id).map((item) => { const { plan: _p, ...o } = item as Offer & { plan: PublicPlan }; return o as Offer; }) }}
																	className="btn btn-outline-primary btn-sm"
																	title={copy('View all offers for this plan', 'Alle Angebote für diesen Plan ansehen', 'Sve ponude za ovaj plan')}
																>
																	<i className="bi bi-eye me-1"></i>{copy('View', 'Ansehen', 'Prikaži')}
																</Link>
																{offer.status === 'pending' && (
																	<>
																		<button
																			className="btn btn-success btn-sm"
																			onClick={(e) => {
																				e.preventDefault();
																				handleOfferAction(offer, 'accept');
																			}}
																			title={copy('Accept offer', 'Angebot annehmen', 'Prihvati ponudu')}
																			disabled={offer.status !== 'pending'}
																		>
																			<i className="bi bi-check"></i>
																		</button>
																		<button
																			className="btn btn-outline-secondary btn-sm"
																			onClick={(e) => {
																				e.preventDefault();
																				handleOfferAction(offer, 'reject');
																			}}
																			title={copy('Reject offer', 'Angebot ablehnen', 'Odbij ponudu')}
																			disabled={offer.status !== 'pending'}
																		>
																			<i className="bi bi-x"></i>
																		</button>
																	</>
																)}
															</div>
														</td>
													</tr>
													{isExpanded && (
														<tr>
															<td colSpan={9} className="p-0">
																<OfferDetailPreview
																	offer={offer}
																	plan={offer.plan}
																	viewAs="seeker"
																	planId={planDetailId(offer.plan)}
																/>
															</td>
														</tr>
													)}
												</Fragment>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
					</div>
				</div>
			)}
			
			{/* Plans Tab Content (all, active, drafts, closed - not offers) */}
			{activeTab !== 'offers' && myPlans.length > 0 && (
				<>
					{filteredPlans.length === 0 ? (
						<div className="card">
							<div className="card-body text-center py-5 text-muted">
								{copy('No plans match your filters yet.', 'Keine Pläne entsprechen Ihren Filtern.', 'Nijedan plan ne odgovara vašim filtrima.')}
							</div>
						</div>
					) : (
				<div className="row g-4">
					{filteredPlans.map(plan => {
					const offersForPlan = (offersByPlan[plan.id] ?? []).sort((a, b) => {
						const statusOrder: Record<Offer['status'], number> = { pending: 0, accepted: -1, rejected: 1 };
						return statusOrder[a.status] - statusOrder[b.status];
					});
						const offerCount = offersForPlan.length;
					const acceptedOffer = offersForPlan.find(o => o.status === 'accepted');
					
					return (
						<div key={plan.id} className="col-12">
							<Link
								to={`/app/seeker/plan/${planDetailId(plan)}`}
								className="text-decoration-none d-block w-100"
								style={{ color: 'inherit' }}
							>
								<div className="card border-0 shadow-sm overflow-hidden plan-card">
									<div className="row g-0">
									{/* Image column: .ratio direct children get BS aspect box — img must be direct child */}
									<div className="col-12 col-md-4 bg-light">
										<div className="ratio ratio-4x3">
											<MyPlanCardCoverImage plan={plan} alt={plan.title} />
											<div
												className="d-flex align-items-end p-3"
												style={{
													zIndex: 1,
													pointerEvents: 'none',
													background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)'
												}}
											>
												<span className={`badge ${getStatusBadgeClass(plan.status)}`}>
													{localizePlanStatus(plan.status)}
												</span>
											</div>
										</div>
									</div>
									
									{/* Content Column */}
									<div className="col-12 col-md-8">
										<div className="card-body p-4">
											<div className="d-flex justify-content-between align-items-start mb-3">
												<div className="flex-grow-1">
													<h2 className="h5 mb-2 fw-bold">{plan.title}</h2>
													<div className="d-flex flex-wrap gap-3 text-muted small mb-2">
														<span><i className="bi bi-geo-alt me-1"></i>{plan.city}</span>
														<span><i className="bi bi-palette me-1"></i>{localizeStyleLabel(plan.styleKey)}</span>
														<span><i className="bi bi-calendar3 me-1"></i>{new Date(plan.createdAt).toLocaleDateString(locale)}</span>
													</div>
													{plan.moderationStatus === 'hidden' && (
														<span className="badge text-bg-danger">{copy('Hidden by admin', 'Vom Admin verborgen', 'Skriveno od admina')}</span>
													)}
												</div>
											</div>

											{/* Quick Stats */}
											<div className="d-flex flex-wrap gap-3 mb-3 pb-3 border-bottom">
												<div className="d-flex align-items-center gap-2">
													<div className="bg-light rounded-circle p-2">
														<i className="bi bi-envelope-paper text-primary"></i>
													</div>
													<div>
														<div className="small text-muted">{copy('Offers', 'Angebote', 'Ponude')}</div>
														<div className="fw-bold">{offerCount}</div>
													</div>
												</div>
												{acceptedOffer && (
													<div className="d-flex align-items-center gap-2">
														<div className="bg-light rounded-circle p-2">
															<i className="bi bi-check-circle text-success"></i>
														</div>
														<div>
															<div className="small text-muted">{copy('Accepted Offer', 'Angenommenes Angebot', 'Prihvaćena ponuda')}</div>
															<div className="fw-bold text-success">
																{acceptedOffer.bands.mid.toLocaleString()} €
															</div>
														</div>
													</div>
												)}
												{plan.questionnaire?.gardenSize && (
													<div className="d-flex align-items-center gap-2">
														<div className="bg-light rounded-circle p-2">
															<i className="bi bi-rulers text-info"></i>
														</div>
														<div>
															<div className="small text-muted">{copy('Garden Size', 'Gartengröße', 'Veličina vrta')}</div>
															<div className="fw-bold">{plan.questionnaire.gardenSize} m²</div>
														</div>
													</div>
												)}
											</div>

											{/* Action Buttons */}
											<div
												className="d-flex flex-wrap gap-2 mb-3 plan-card-actions"
												onClick={(e) => e.stopPropagation()}
											>
												<Link 
													to={`/app/seeker/plan/${planDetailId(plan)}`}
													className="btn btn-success btn-sm"
													onClick={(e) => e.stopPropagation()}
												>
													<i className="bi bi-eye me-1"></i>{copy('View details', 'Details anzeigen', 'Prikaži detalje')}
												</Link>
												{plan.status !== 'draft' && offerCount > 0 && (
													<Link to={`/app/seeker/plan/${planDetailId(plan)}/offers`} className="btn btn-outline-success btn-sm" onClick={(e) => e.stopPropagation()}>
														<i className="bi bi-envelope-paper me-1"></i>{copy('View Offers', 'Angebote anzeigen', 'Prikaži ponude')} ({offerCount})
													</Link>
												)}
												{plan.status === 'draft' && (
													<>
														<button
															type="button"
															className="btn btn-outline-secondary btn-sm"
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																handleEditPlan(plan);
															}}
															disabled={plan.canEdit === false}
															title={plan.canEdit === false ? copy('Company has disabled editing for this draft', 'Die Firma hat die Bearbeitung dieses Entwurfs deaktiviert', 'Tvrtka je onemogućila uređivanje ovog nacrta') : undefined}
														>
															<i className="bi bi-pencil me-1"></i>{copy('Edit', 'Bearbeiten', 'Uredi')}
														</button>
														<button
															type="button"
															className="btn btn-outline-danger btn-sm"
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																handleDeletePlan(plan.id, plan.title);
															}}
														>
															<i className="bi bi-trash me-1"></i>{copy('Delete', 'Löschen', 'Izbriši')}
														</button>
													</>
												)}
											</div>

											{/* Offers Preview */}
											{offersForPlan.length > 0 && (
												<div className="mt-3">
													<h6 className="small text-muted text-uppercase mb-2">{copy('Recent Offers', 'Neueste Angebote', 'Nedavne ponude')}</h6>
													<div className="table-responsive">
														<table className="table table-sm table-hover align-middle mb-0">
															<thead className="table-light">
																<tr>
																	<th className="small">{copy('Gardener', 'Gärtner', 'Vrtlar')}</th>
																	<th className="small">{copy('Estimated price', 'Geschätzter Preis', 'Procijenjena cijena')}</th>
																	<th className="small">{copy('Status', 'Status', 'Status')}</th>
																	<th className="small text-end">{copy('Actions', 'Aktionen', 'Radnje')}</th>
																</tr>
															</thead>
															<tbody>
																{offersForPlan.slice(0, 3).map(offer => (
																	<tr key={offer.id}>
																		<td>
																			<div className="fw-medium">{offer.gardenerName}</div>
																			{offer.message && (
																				<div className="small text-muted" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
																					{offer.message}
																				</div>
																			)}
																		</td>
																		<td>
																			<div className="small fw-medium">€{(offer.bands.mid || offer.bands.min).toLocaleString()}</div>
																		</td>
																		<td>
																			<span className={`badge ${offer.status === 'accepted' ? 'text-bg-success' : offer.status === 'rejected' ? 'text-bg-danger' : 'text-bg-warning'}`}>
																				{localizeOfferStatus(offer.status)}
																			</span>
																		</td>
																		<td className="text-end">
																			<div className="btn-group btn-group-sm" onClick={(e) => e.stopPropagation()}>
																				<button
																					type="button"
																					className="btn btn-success btn-sm"
																					onClick={(e) => {
																						e.preventDefault();
																						e.stopPropagation();
																						handleOfferAction(offer, 'accept');
																					}}
																					disabled={offer.status !== 'pending'}
																					title={copy('Accept offer', 'Angebot annehmen', 'Prihvati ponudu')}
																				>
																					<i className="bi bi-check"></i>
																				</button>
																				<button
																					type="button"
																					className="btn btn-outline-secondary btn-sm"
																					onClick={(e) => {
																						e.preventDefault();
																						e.stopPropagation();
																						handleOfferAction(offer, 'reject');
																					}}
																					disabled={offer.status !== 'pending'}
																					title={copy('Reject offer', 'Angebot ablehnen', 'Odbij ponudu')}
																				>
																					<i className="bi bi-x"></i>
																				</button>
																			</div>
																		</td>
																	</tr>
																))}
																{offersForPlan.length > 3 && (
																	<tr>
																		<td colSpan={4} className="text-center py-2">
																			<Link to={`/app/seeker/plan/${planDetailId(plan)}/offers`} className="small text-primary text-decoration-none" onClick={(e) => e.stopPropagation()}>
																				View all {offersForPlan.length} offers <i className="bi bi-arrow-right"></i>
																			</Link>
																		</td>
																	</tr>
																)}
															</tbody>
														</table>
													</div>
												</div>
											)}
											{offersForPlan.length === 0 && plan.status !== 'draft' && (
												<div className="text-center py-3 text-muted small">
													<i className="bi bi-inbox fs-4 d-block mb-2 opacity-50"></i>
													{copy('No offers yet. Your plan is visible to gardeners.', 'Noch keine Angebote. Ihr Plan ist für Gärtner sichtbar.', 'Još nema ponuda. Vaš plan je vidljiv vrtlarima.')}
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
							</Link>
						</div>
					);
				})}
				</div>
					)}
				</>
			)}
			{messageOfferId && user && (() => {
				const offerIdNum = parseInt(messageOfferId, 10);
				const userIdNum =
					planOwnerUserIdNum ??
					(typeof user.id === 'string' ? parseInt(user.id, 10) : user.id);
				const offer = myOffers.find((o) => o.id === messageOfferId);
				if (isNaN(offerIdNum) || userIdNum == null || isNaN(userIdNum)) return null;
				return (
					<>
						<div className="modal fade show" style={{ display: 'block', zIndex: 20000 }} role="dialog" aria-modal="true" aria-labelledby="myPlansOfferMessageTitle">
							<div className="modal-dialog modal-dialog-centered">
								<div className="modal-content">
									<div className="modal-header">
										<h5 className="modal-title" id="myPlansOfferMessageTitle">
											{copy('Message', 'Nachricht', 'Poruka')} {offer?.gardenerName ?? copy('gardener', 'Gärtner', 'vrtlar')}
										</h5>
										<button type="button" className="btn-close" aria-label="Close" onClick={() => setMessageOfferId(null)} />
									</div>
									<div className="modal-body">
										<ConversationThread
											variant="offer"
											offerId={offerIdNum}
											currentUserId={userIdNum}
											otherPartyName={offer?.gardenerName}
											onClose={() => setMessageOfferId(null)}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-backdrop fade show" style={{ zIndex: 19990 }} />
					</>
				);
			})()}
		</>
	);
}
